import { MenuItem } from '../config/menuConfig';

export interface MenuPermissionOptions {
  requireAll?: boolean; // true = AND condition, false = OR condition (default)
}

/**
 * Check if user has permission to access a menu item
 * @param menuItem - The menu item to check
 * @param userPermissions - Array of user's permissions
 * @param options - Permission checking options
 */
export const hasMenuPermission = (
  menuItem: MenuItem, 
  userPermissions: string[],
  options: MenuPermissionOptions = { requireAll: false }
): boolean => {
  // If no permissions required, allow access
  if (!menuItem.requiredPermissions || menuItem.requiredPermissions.length === 0) {
    return true;
  }

  // Check if user is super admin (has all permissions)
  if (userPermissions.includes('*') || userPermissions.includes('superadmin')) {
    return true;
  }

  // Check permissions based on requirement type
  if (options.requireAll) {
    // AND condition - user must have ALL required permissions
    return menuItem.requiredPermissions.every(permission => 
      userPermissions.includes(permission)
    );
  } else {
    // OR condition - user must have AT LEAST ONE required permission
    return menuItem.requiredPermissions.some(permission => 
      userPermissions.includes(permission)
    );
  }
};

/**
 * Filter menu items based on user permissions
 * @param menuItems - Array of menu items to filter
 * @param userPermissions - Array of user's permissions
 */
export const filterMenuByPermissions = (
  menuItems: MenuItem[], 
  userPermissions: string[]
): MenuItem[] => {
  return menuItems.reduce((filteredMenu: MenuItem[], item) => {
    // Check if user has permission for this menu item
    if (hasMenuPermission(item, userPermissions)) {
      const filteredItem: MenuItem = { ...item };

      // If item has children, filter them recursively
      if (item.children) {
        filteredItem.children = filterMenuByPermissions(item.children, userPermissions);
        
        // Only include parent if it has accessible children or its own path
        if (filteredItem.children.length > 0 || filteredItem.path) {
          filteredMenu.push(filteredItem);
        }
      } else {
        filteredMenu.push(filteredItem);
      }
    }

    return filteredMenu;
  }, []);
};

/**
 * Get all accessible paths for a user based on their permissions
 * @param menuItems - Array of menu items
 * @param userPermissions - Array of user's permissions
 */
export const getUserAccessiblePaths = (
  menuItems: MenuItem[], 
  userPermissions: string[]
): string[] => {
  const paths: string[] = [];

  const extractPaths = (items: MenuItem[]) => {
    items.forEach(item => {
      if (hasMenuPermission(item, userPermissions) && item.path) {
        paths.push(item.path);
      }
      if (item.children) {
        extractPaths(item.children);
      }
    });
  };

  extractPaths(menuItems);
  return paths;
};

/**
 * Check if a specific path is accessible to the user
 * @param path - The path to check
 * @param userPermissions - Array of user's permissions
 * @param menuItems - Array of menu items
 */
export const isPathAccessible = (
  path: string, 
  userPermissions: string[], 
  menuItems: MenuItem[]
): boolean => {
  const accessiblePaths = getUserAccessiblePaths(menuItems, userPermissions);
  return accessiblePaths.includes(path);
};

/**
 * Find menu item by path
 * @param path - The path to find
 * @param menuItems - Array of menu items to search
 */
export const findMenuItemByPath = (path: string, menuItems: MenuItem[]): MenuItem | null => {
  for (const item of menuItems) {
    if (item.path === path) {
      return item;
    }
    if (item.children) {
      const found = findMenuItemByPath(path, item.children);
      if (found) return found;
    }
  }
  return null;
};
